/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';

import { truncate } from 'lodash';

import config from 'js/app/config';
import Retracked from 'js/app/retracked';
import path from 'js/lib/path';

import { Grid, PageGridContainer, Typography } from '@coursera/cds-core';

import PaginationControls from 'bundles/page/components/cds/PaginationControls';
import IsMobileScreenContext from 'bundles/page/contexts/IsMobileScreenContext';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import ArticleAuthorProfile from 'bundles/shared-articles/components/ArticleAuthorProfile';
import AuthorPageGoogleSchemaMarkup from 'bundles/shared-articles/components/seo/AuthorPageGoogleSchemaMarkup';
import useArticleUrlData from 'bundles/shared-articles/hooks/useArticleUrlData';
import { getPageNumberFromString } from 'bundles/shared-articles/utils';
import { generatePaginationProperties } from 'bundles/shared-articles/utils/seoUtils';
import AuthorKnowsAbout from 'bundles/unified-seo-content-common/components/author-page/AuthorKnowsAbout';
import AuthorPageSkeleton from 'bundles/unified-seo-content-common/components/author-page/AuthorPageSkeleton';
import ArticleHubCardWithEstimatedReadTime from 'bundles/unified-seo-content-common/components/hub-page/ArticleHubCardWithEstimatedReadTime';
import PageDataErrorHandler from 'bundles/unified-seo-content-common/components/shared/PageDataErrorHandler';
import { useArticlesAuthorPageQuery } from 'bundles/unified-seo-content-common/hooks/useArticlesAuthorPageQuery';
import { scrollPageToTop } from 'bundles/unified-seo-content-common/utils';

import _t from 'i18n!nls/articles';

const styles = {
  pageWrapper: css`
    margin: var(--cds-spacing-400) auto;
    min-height: 50vh;
    max-width: 1022px;
  `,
  pagination: css`
    margin: var(--cds-spacing-400) 0;
  `,
};

const MAX_NUM_ARTICLES_DESKTOP = 6;
const MAX_NUM_ARTICLES_MOBILE = 4;

const ArticleAuthorPage = () => {
  const { isMobileScreen } = React.useContext(IsMobileScreenContext);
  const { page, slug, isPreview, pathname } = useArticleUrlData();
  const currentPageNumber = getPageNumberFromString(page);

  const pageArticlesLimit = isMobileScreen ? MAX_NUM_ARTICLES_MOBILE : MAX_NUM_ARTICLES_DESKTOP;
  const skipArticles = (currentPageNumber - 1) * pageArticlesLimit;

  const { isPageLoading, error, author, articles, articlesCount } = useArticlesAuthorPageQuery({
    slug,
    preview: isPreview,
    numberOfArticles: pageArticlesLimit,
    skipArticles,
  });

  const pageUrl = path.join(config.url.domain, pathname);
  const pageCount = articlesCount ? Math.ceil(articlesCount / pageArticlesLimit) : 0;

  useEffect(() => {
    scrollPageToTop();
  }, [currentPageNumber]);

  if (isPageLoading) {
    return <AuthorPageSkeleton isMobileScreen={isMobileScreen} maxArticles={pageArticlesLimit} />;
  }

  if (!author || error) {
    return <PageDataErrorHandler />;
  }

  const metaDescription = author?.description ? truncate(author.description, { length: 250 }) : _t('Coursera Author');
  const metaTitle = _t('#{authorName}, #{authorJobTitle} at Coursera', {
    authorName: author?.name || _t('Coursera Writer'),
    authorJobTitle: author?.jobTitle || _t('Author'),
  });

  const paginationProperties = generatePaginationProperties({
    pageCount,
    currentPageNumber,
    pageUrl,
  });

  return (
    <PageGridContainer css={styles.pageWrapper}>
      <Grid container spacing={isMobileScreen ? 8 : 16}>
        <Grid item xs={12}>
          <ArticleAuthorProfile {...author} />
        </Grid>
        {!!author?.knowsAbout?.length && <AuthorKnowsAbout isMobileScreen={isMobileScreen} items={author.knowsAbout} />}
        {!!articles?.length && (
          <Grid item xs={12}>
            <Grid container spacing={isMobileScreen ? 8 : 16}>
              <Grid item xs={12}>
                <Typography variant="h2semibold">{_t('Articles')}</Typography>
              </Grid>
              {articles.map(
                (article) =>
                  article?.slug && (
                    <Grid item xs={6} md={4} key={`${article.slug}${article?.localizedCountryCode ?? ''}`}>
                      <ArticleHubCardWithEstimatedReadTime
                        title={article.title || _t('Coursera Article')}
                        subtitle={article.intro || _t('This is a Coursera Article')}
                        slug={article.slug}
                        localizedCountryCode={article?.localizedCountryCode ?? undefined}
                        metadata={{
                          publishedDate: article.sys.firstPublishedAt,
                        }}
                        image={{
                          url: article.featuredImage?.url || '',
                          description: article.featuredImage?.description || _t('Article Image'),
                        }}
                      />
                    </Grid>
                  )
              )}
            </Grid>
          </Grid>
        )}
        {articlesCount > pageArticlesLimit && (
          <Grid item xs={12}>
            <Grid container justifyContent="center" css={styles.pagination}>
              <PaginationControls
                pageCount={pageCount}
                currentPage={currentPageNumber}
                renderLinks
                largeStyle
                maxPages={5}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
      <AuthorPageGoogleSchemaMarkup author={author} id={pageUrl} />
      <CourseraMetatags
        metaNameAndProperties={{
          title: metaTitle,
          ogAndTwitterTitle: metaTitle,
          description: metaDescription,
        }}
        paginationProperties={paginationProperties}
      />
    </PageGridContainer>
  );
};

export default Retracked.createTrackedContainer(() => ({
  namespace: {
    app: 'articles',
    page: 'article_author_page',
  },
}))(ArticleAuthorPage);
